import { useMemo } from 'react';
import type { PaddingOptions } from 'maplibre-gl';

import { useSlidePanelWidth } from '@/hooks';

/**
 * Assembles the configurator preview's MapLibre camera padding so the center
 * coordinate (and thus the draggable {@link AnchorMarker}) is framed in the
 * *visible* map area — left of the open SlidePanel on desktop, above the partial
 * bottom sheet plus its stacked overlays on mobile.
 *
 * Mirrors the main map's persistent `<Map padding>` prop (see MapInstance.tsx):
 * with padding set, every camera operation (initial view, country/address
 * `flyTo`, zoom `easeTo`) automatically frames the coordinate in the free area.
 *
 * The desktop axis uses the raw panel width (no margin, `0` on mobile) so the
 * pin shares the exact horizontal center used by the overlays'
 * `(100% - panelWidth) / 2` math; the mobile `bottomInset` is owned and computed
 * once by {@link useMapConfigOverlays} (same panel-state branch as the overlay
 * offset), keeping a single source of truth for the occupied bottom region.
 */
export function useMapConfigCameraPadding(bottomInset: number): PaddingOptions {
  const panelWidth = useSlidePanelWidth();

  return useMemo(
    () => ({ top: 0, bottom: bottomInset, left: 0, right: panelWidth }),
    [bottomInset, panelWidth]
  );
}
